import {
  accessScaleOnly,
  assessmentScaleOf,
  mergeWidaRosters,
  parseWidaHistory,
  scaleForUploadKind,
  type AssessmentScale,
} from '../Upload/widaHistory';
import { loadWidaRoster, saveWidaRoster } from '../Upload/studentRoster';
import {
  addSisResolvedIds,
  clearSisResolvedIds,
  countSisDataRows,
  hasSisRosterMappedFlag,
  loadSisResolvedIds,
  loadSisRoster,
  mergeSisRosters,
  parseSisRosterRows,
  saveSisRoster,
  saveSisRosterMapped,
  sisLoadBearingColumnsResolved,
  sisStudentCovered,
} from '../Upload/sisRoster';
import { applySavedSisMapping, readSisColumns } from '../Upload/sisFieldMapping';
import { hydrateSisMappings } from './sisMappingApi';
import { FILE_TYPE_OPTIONS } from '../Upload/fileTypes';
import { fetchUploadCsv, fetchUploads } from './uploadsApi';
import { labelForEndYear } from '../../core/schoolYear';

export type UploadKind = 'wida_access' | 'alternate_access_wida' | 'student_roster';

export type UploadedFile = {
  id: string;
  name: string;
  kind?: UploadKind;
  schoolYear?: string;
  uploadedAt?: string;
  clientKey?: string;
};

export function isWidaKind(kind: UploadKind | undefined): boolean {
  return kind === 'wida_access' || kind === 'alternate_access_wida';
}

export const UPLOAD_KIND_OPTIONS: ReadonlyArray<{ value: UploadKind; label: string }> =
  FILE_TYPE_OPTIONS;

function widaDataSchoolYear(): string | null {
  let end: number | null = null;
  for (const student of accessScaleOnly(loadWidaRoster())) {
    for (const record of student.years) {
      if (end == null || record.year > end) end = record.year;
    }
  }
  if (end == null) return null;
  return labelForEndYear(end);
}

export type RosterSource = {
  csv: string;
  kind: UploadKind;
  schoolYear?: string;
  uploadedAt?: string;
};

const stamp = (i: { uploadedAt?: string }) => i.uploadedAt ?? '9999';
const newestFirst = (a: { uploadedAt?: string }, b: { uploadedAt?: string }) =>
  stamp(b).localeCompare(stamp(a));
const oldestFirst = (a: { uploadedAt?: string }, b: { uploadedAt?: string }) =>
  stamp(a).localeCompare(stamp(b));

function maxStamp<T extends { schoolYear?: string }>(files: T[]): string | null {
  return files.reduce<string | null>(
    (max, f) => (f.schoolYear != null && (max == null || f.schoolYear > max) ? f.schoolYear : max),
    null,
  );
}

function selectSisSources<T extends { kind?: UploadKind; schoolYear?: string }>(
  files: T[],
  anchor: string | null,
): T[] {
  const sis = files.filter((f) => f.kind === 'student_roster');
  const stampMax = maxStamp(sis);
  const widaStamp = maxStamp(files.filter((f) => f.kind === 'wida_access'));
  const hasAnchorYear = anchor != null && sis.some((f) => f.schoolYear === anchor);
  const qualifying = sis.filter((f) => {
    if (f.schoolYear == null) return false;
    if (anchor != null) return f.schoolYear === (hasAnchorYear ? anchor : widaStamp);
    if (widaStamp != null) return f.schoolYear === widaStamp;
    return stampMax == null || f.schoolYear === stampMax;
  });
  return qualifying.length > 0 ? qualifying : sis.filter((f) => f.schoolYear == null);
}

function anchorSchoolYearFromWidaSources(sources: RosterSource[]): string | null {
  let end: number | null = null;
  const parsed = mergeWidaRosters(
    sources
      .filter((s) => isWidaKind(s.kind))
      .flatMap((s) => parseWidaHistory(s.csv, scaleForUploadKind(s.kind))),
  );
  for (const student of accessScaleOnly(parsed)) {
    for (const record of student.years) {
      if (end == null || record.year > end) end = record.year;
    }
  }
  return end == null ? null : `${end - 1}-${String(end % 100).padStart(2, '0')}`;
}

const SCALE_RETRY_KEY = 'bamboo.widaScaleRetry.v1';

function scalesNeedingRetry(): Set<AssessmentScale> {
  try {
    const raw = window.sessionStorage.getItem(SCALE_RETRY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? (parsed as AssessmentScale[]) : []);
  } catch {
    return new Set();
  }
}

function saveScaleRetries(scales: Set<AssessmentScale>): void {
  try {
    window.sessionStorage.setItem(SCALE_RETRY_KEY, JSON.stringify([...scales]));
  } catch {
    return;
  }
}

async function fetchSources(
  files: (UploadedFile & { kind: UploadKind })[],
): Promise<{ sources: (RosterSource & { id: string })[]; failedIds: string[] }> {
  const results = await Promise.allSettled(
    files.map(async (f) => ({
      id: f.id,
      kind: f.kind,
      schoolYear: f.schoolYear,
      uploadedAt: f.uploadedAt,
      csv: (await fetchUploadCsv(f.id)).csv,
    })),
  );
  return {
    sources: results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])),
    failedIds: files.filter((_, i) => results[i]?.status === 'rejected').map((f) => f.id),
  };
}

let hydrationGeneration = 0;

export function invalidateRosterHydration(): void {
  hydrationGeneration += 1;
}

export async function hydrateRostersFromUploads(
  prefetched?: UploadedFile[],
  isDeleted?: (id: string) => boolean,
  opts?: { force?: boolean },
): Promise<void> {
  const gen = ++hydrationGeneration;
  const superseded = () => gen !== hydrationGeneration;
  const abortIfSuperseded = () => {
    if (superseded() && opts?.force) throw new Error('wida hydration superseded');
    return superseded();
  };
  if (opts?.force) {
    await hydrateSisMappings();
    if (abortIfSuperseded()) return;
    const remote = prefetched ?? (await fetchUploads());
    if (abortIfSuperseded()) return;
    const typed = remote.filter((f): f is UploadedFile & { kind: UploadKind } => f.kind != null);
    const live = (s: { id: string }) => !(isDeleted?.(s.id) ?? false);
    const wantedWida = typed.filter((f) => isWidaKind(f.kind) && live(f));
    const wida = await fetchSources(wantedWida);
    if (abortIfSuperseded()) return;
    const failedScales = new Set(
      wantedWida
        .filter((f) => wida.failedIds.includes(f.id) && live(f))
        .map((f) => scaleForUploadKind(f.kind)),
    );
    saveScaleRetries(new Set([...failedScales]));
    if (failedScales.has('access') || (opts?.force && failedScales.size > 0)) {
      throw new Error('wida hydration incomplete');
    }
    const widaSources = wida.sources
      .filter(live)
      .filter((s) => !failedScales.has(scaleForUploadKind(s.kind)));
    const anchor = anchorSchoolYearFromWidaSources(widaSources);
    const wantedSis = selectSisSources(typed.filter(live), anchor);
    const sis = await fetchSources(wantedSis);
    if (abortIfSuperseded()) return;
    if (sis.failedIds.some((id) => live({ id }))) {
      throw new Error('sis hydration incomplete');
    }
    rebuildRostersFromCsvs([...widaSources, ...sis.sources.filter(live)]);
    return;
  }
  const cached = loadWidaRoster();
  const missingScales = scalesNeedingRetry();
  if (opts?.force || accessScaleOnly(cached).length === 0) missingScales.add('access');
  if (!cached.some((s) => (s.years ?? []).some((y) => assessmentScaleOf(y) === 'alternate_access'))) {
    missingScales.add('alternate_access');
  }
  if (opts?.force) saveWidaRoster([]);
  const needWida = missingScales.has('access');
  const needSis = loadSisRoster().length === 0 || !hasSisRosterMappedFlag();
  const topUpScale = prefetched != null && missingScales.size > 0;
  if (!needWida && !needSis && !topUpScale) return;
  const anchorBefore = widaDataSchoolYear();
  const sisBefore = JSON.stringify(loadSisRoster());
  const remote = prefetched ?? (await fetchUploads());
  if (abortIfSuperseded()) return;
  const typed = remote.filter((f): f is UploadedFile & { kind: UploadKind } => f.kind != null);
  const live = (s: { id: string }) => !(isDeleted?.(s.id) ?? false);
  let sisTouchedMidFlight = false;
  if (missingScales.size > 0) {
    const wanted = typed.filter(
      (f) => isWidaKind(f.kind) && missingScales.has(scaleForUploadKind(f.kind)) && live(f),
    );
    const wida = await fetchSources(wanted);
    if (abortIfSuperseded()) return;
    const failedScales = new Set(
      wanted
        .filter((f) => wida.failedIds.includes(f.id) && live(f))
        .map((f) => scaleForUploadKind(f.kind)),
    );
    saveScaleRetries(new Set([...missingScales].filter((s) => failedScales.has(s))));
    if (failedScales.has('access')) {
      throw new Error('wida hydration incomplete');
    }
    sisTouchedMidFlight = JSON.stringify(loadSisRoster()) !== sisBefore;
    wida.sources
      .filter(live)
      .filter((s) => !failedScales.has(scaleForUploadKind(s.kind)))
      .sort(newestFirst)
      .forEach(({ csv, kind }) => importCsvIntoRoster(csv, kind));
  }
  const anchorEstablished = anchorBefore == null && widaDataSchoolYear() != null;
  const needSisAfterImports = needSis || loadSisRoster().length === 0 || !hasSisRosterMappedFlag();
  if (needSisAfterImports || anchorEstablished) {
    const anchor = widaDataSchoolYear();
    const sis = await fetchSources(selectSisSources(typed.filter(live), anchor));
    if (abortIfSuperseded()) return;
    const stillNeedSis = loadSisRoster().length === 0 || !hasSisRosterMappedFlag();
    const sisChanged = sisTouchedMidFlight || JSON.stringify(loadSisRoster()) !== sisBefore;
    const rebuildForAnchor = anchorEstablished && !sisChanged;
    const fetchOk = sis.failedIds.every((id) => !live({ id }));
    const liveSources = sis.sources.filter(live);
    const importSources = () =>
      liveSources
        .sort(oldestFirst)
        .forEach(({ csv, kind, schoolYear }) =>
          importCsvIntoRoster(csv, kind, anchor != null ? schoolYear : undefined),
        );
    if (rebuildForAnchor) {
      invalidateSisRoster();
      if (fetchOk) {
        importSources();
      }
    } else if (stillNeedSis && !sisTouchedMidFlight && fetchOk && liveSources.length > 0) {
      invalidateSisRoster();
      importSources();
    }
  }
}

export function invalidateSisRoster(): void {
  saveSisRoster([]);
  saveSisRosterMapped(false);
  clearSisResolvedIds();
}

export function rebuildRostersFromCsvs(inputs: RosterSource[]): void {
  saveWidaRoster([]);
  invalidateSisRoster();
  inputs
    .filter((i) => isWidaKind(i.kind))
    .sort(newestFirst)
    .forEach(({ csv, kind }) => importCsvIntoRoster(csv, kind));
  selectSisSources(inputs, widaDataSchoolYear())
    .sort(oldestFirst)
    .forEach(({ csv, kind }) => importCsvIntoRoster(csv, kind));
}

export function importSisSources(sources: RosterSource[]): void {
  const anchor = widaDataSchoolYear();
  sources
    .filter((s) => s.kind === 'student_roster')
    .sort(oldestFirst)
    .forEach(({ csv, kind, schoolYear }) =>
      importCsvIntoRoster(csv, kind, anchor != null ? schoolYear : undefined),
    );
}

export function importCsvIntoRoster(csvText: string, kind: UploadKind, schoolYear?: string): boolean {
  if (isWidaKind(kind)) {
    const anchorBefore = widaDataSchoolYear();
    saveWidaRoster(
      mergeWidaRosters([
        ...loadWidaRoster(),
        ...parseWidaHistory(csvText, scaleForUploadKind(kind)),
      ]),
    );
    const anchorMoved = widaDataSchoolYear() !== anchorBefore;
    if (anchorMoved) {
      invalidateSisRoster();
    }
    return anchorMoved;
  }
  const anchor = widaDataSchoolYear();
  if (schoolYear != null && (anchor == null || schoolYear < anchor)) {
    return false;
  }
  const saved = applySavedSisMapping(readSisColumns(csvText));
  if (saved.kind === 'partial' && Object.keys(saved.mapping).length > 0) {
    if (countSisDataRows(csvText) > 0) {
      saveSisRosterMapped(false);
    }
    return false;
  }
  const mapping = saved.kind === 'exact' ? saved.mapping : undefined;
  const existing = loadSisRoster();
  const parsedRows = parseSisRosterRows(csvText, mapping);
  const parsed = mergeSisRosters(parsedRows);
  const merged = mergeSisRosters([...existing, ...parsed]);
  saveSisRoster(merged);
  if (sisLoadBearingColumnsResolved(csvText, mapping)) {
    addSisResolvedIds(parsed.flatMap((s) => [s.studentId, ...s.altIds]));
  }
  const droppedRows = parsedRows.length < countSisDataRows(csvText);
  const resolvedIds = loadSisResolvedIds();
  const inResolved = (s: { studentId: string; altIds: string[] }) =>
    [s.studentId, ...s.altIds].some((id) => resolvedIds.has(id));
  saveSisRosterMapped(
    !droppedRows && merged.length > 0 && merged.every(inResolved) && merged.every(sisStudentCovered),
  );
  return false;
}

export function isCsvFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.csv');
}

let sequence = 0;

export function toUploadedFiles(files: ArrayLike<File>): UploadedFile[] {
  return Array.from(files)
    .filter(isCsvFile)
    .map((file) => {
      sequence += 1;
      return {
        id: `${file.name}-${file.size}-${file.lastModified}-${sequence}`,
        name: file.name,
      };
    });
}

export function removeFileById(files: UploadedFile[], id: string): UploadedFile[] {
  return files.filter((file) => file.id !== id);
}

export function dedupeById(files: UploadedFile[]): UploadedFile[] {
  const seen = new Set<string>();
  return files.filter((file) => {
    if (seen.has(file.id)) return false;
    seen.add(file.id);
    return true;
  });
}
